import { useProjectParams } from '@hooks/useProjectParams';
import { ChooseSectionText } from 'components/ChooseSectionText';
import { Toolbar } from 'components/Toolbar';
import { WorkOrderConstructionTypesTabSelect } from 'components/WorkOrderConstructionTypesTabSelect';
import { filtersWorkOrderSlice } from 'features/filtersWorkOrder/filtersWorkOrderSlice';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ActKc2NavData, WorkOrderVersionItem } from 'gql/graphql';
import { ActKc2StatusEnum, ContractStatusEnum, ProjectStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { useProjectData } from 'hooks/useProjectData';
import { ProjectPageLayout } from 'pages/Project/Common/Layouts/ProjectPageLayout';
import { getWorkOrderVersionListItems } from 'pages/Project/Common/utils';
import { Button, cnMixSpace, Grid, GridItem, IconBackward } from 'uikit';
import { isNumber } from 'utils';
import { getLabelWorkOrder } from 'utils/getLabelWorkOrder';

import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { SelectVersionsWorkOrder } from '../../Common/WorkOrderVersionSelect';
import { useConstructionTypes } from '../WorkOrder/hooks/useConstructionTypes';
import { WorkOrderFilterForm } from '../WorkOrder/WorkOrderFilterForm';
import { PAGINATION_HEIGHT, TABLE_SETTINGS_HEIGHT } from '../WorkOrder/WorkOrderPrice';
import { workOrderVersionsGql } from '../WorkOrder/workOrderQueries';
import { WorkOrderChapters } from '../WorkOrder/workorderTypes';

import { useWorkOrderVersionObjects } from './hooks/useWorkOrderVersionObjects';
import { useWorkOrderVersionSeasons } from './hooks/useWorkOrderVersionSeasons';
import { WorkOrderVersionOtherTable } from './WorkOrderVersionOthersTable';
import { WorkOrderVersionPrice } from './WorkOrderVersionPrice';
import { WorkOrderVersionRequisites } from './WorkOrderVersionRequisites';
import { WorkOrderVersionSummaryTable } from './WorkOrderVersionSummaryTable';

import './WorkOrderVersion.css';

export const WorkOrderVersion = () => {
    const { id: projectId, orderId, versionId, methodology } = useProjectParams();
    const { pathname } = useLocation();

    const refBtn = useRef<HTMLButtonElement>(null);
    const { order: orderRules } = useAccess();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state }: { state: { reload: boolean | number; openEditWorkOrder?: boolean } } = useLocation();

    const { projectData, refreshProject } = useProjectData(projectId);
    const getContractData = projectData?.contractNavigation?.data?.contractNavData;
    const statusProject = projectData?.status;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;
    const isProjectStatusCompleted = statusProject === ProjectStatusEnum.Completed;

    const isDisabledByProjectStatus = isProjectStatusDelayed || isProjectStatusCompleted;

    const isContractStatusWork = getContractData?.status === ContractStatusEnum.InImplementation;
    const workOrderId = Number(orderId);
    const workOrderVersionId = Number(versionId);

    const [constructionTypeId, setConstructionTypeId] = useState(0);
    const [filters, setFilters] = useState<any>({});

    const [constructionTypes, constructionTypesStatus] = useConstructionTypes(workOrderId);

    const [workOrderObjects] = useWorkOrderVersionObjects(workOrderVersionId);

    const variables = useMemo(
        () => ({
            workOrderId,
        }),
        [workOrderId],
    );

    const [workOrderVersionsData] = useRequest({
        requestId: 'workOrderVersions',
        query: workOrderVersionsGql,
        variables,
        withEffect: true,
    });

    const versionSelectItems = useMemo(
        () => getWorkOrderVersionListItems(workOrderVersionsData?.uerWorkOrderVersions),
        [workOrderVersionsData?.uerWorkOrderVersions],
    );

    const activeItemVersion = useMemo(
        () => versionSelectItems?.find(({ id }: { id: number }) => id === workOrderVersionId),
        [versionSelectItems, workOrderVersionId],
    );

    useEffect(() => {
        dispatch(filtersWorkOrderSlice.actions.setFiltersWorkOrder(filters));
    }, [filters, dispatch]);

    useEffect(() => {
        if (state?.openEditWorkOrder) {
            setTimeout(() => refBtn.current?.click(), 500);
        }
    }, [state?.openEditWorkOrder]);

    const isOneOfThePriceChaptersSelected = useMemo(
        () =>
            [WorkOrderChapters.CHAPTER1_7, WorkOrderChapters.CHAPTER8, WorkOrderChapters.CHAPTER9].includes(
                filters.chapter,
            ),
        [filters.chapter],
    );
    const isOthersChapterSelected = filters.chapter === WorkOrderChapters.OTHERS;
    const isSummaryChapterSelected = filters.chapter === WorkOrderChapters.SUMMARY;

    const isShowTablePossible = useMemo(
        () =>
            (isSummaryChapterSelected && filters.chapter) ||
            (isOneOfThePriceChaptersSelected && filters.subObjectId && filters.chapter) ||
            (isOthersChapterSelected && filters.objectId && filters.chapter),
        [
            filters.chapter,
            filters.objectId,
            filters.subObjectId,
            isOneOfThePriceChaptersSelected,
            isOthersChapterSelected,
            isSummaryChapterSelected,
        ],
    );

    const breadCrumbsItems = useMemo(
        () => [
            {
                label: projectData?.name,
                href: `/projects/${methodology}/${projectId}`,
            },
            {
                label: 'Договор',
                href: `/projects/${methodology}/${projectData?.uniqueId}/contract`,
            },
        ],
        [projectData?.name, projectData?.uniqueId, methodology, projectId],
    );

    const firstRenderWorkOrderFilterSetter = useCallback(({ savedValuesObj }) => {
        const savedConstructionTypeId = savedValuesObj?.constructionTypeId;
        const savedFilters = savedValuesObj?.[String(savedConstructionTypeId)];

        if (savedConstructionTypeId) {
            setConstructionTypeId(savedConstructionTypeId);
        }
        if (savedFilters) {
            setFilters(savedFilters);
        }
    }, []);

    const firstRenderWorkOrderEmptyFilterSetter = useMemo(() => {
        if (constructionTypes?.length) {
            return (setter: (params: { key: string | undefined; newState: any }) => void) => {
                const newConstructionTypeId = constructionTypes[0].id;

                setConstructionTypeId(newConstructionTypeId);
                setter({
                    key: 'constructionTypeId',
                    newState: newConstructionTypeId,
                });
            };
        }

        return null;
    }, [constructionTypes]);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: firstRenderWorkOrderFilterSetter,
                withEmptyFilterSetter: firstRenderWorkOrderEmptyFilterSetter,
                unableToProcess: constructionTypesStatus === RequestStatus.FETCHING,
            }),
            [constructionTypesStatus, firstRenderWorkOrderEmptyFilterSetter, firstRenderWorkOrderFilterSetter],
        ),
    );

    const handleConstructionTypeChange = useCallback(
        (newConstructionTypeId) => {
            setConstructionTypeId(newConstructionTypeId);
            setFilters(savedFilterState?.[newConstructionTypeId] ?? {});

            handleSaveFilterState({
                key: 'constructionTypeId',
                newState: newConstructionTypeId,
            });
        },
        [handleSaveFilterState, savedFilterState],
    );

    const handleFilterFormChange = useCallback(
        (newFilters) => {
            let mergedFilters = {};

            setFilters((oldFilters: any) => {
                if (
                    oldFilters.chapter &&
                    oldFilters.chapter !== newFilters.chapter &&
                    newFilters.chapter === WorkOrderChapters.OTHERS
                ) {
                    mergedFilters = { ...oldFilters, ...newFilters, subObjectId: undefined };

                    return mergedFilters;
                }
                if (newFilters.chapter === WorkOrderChapters.SUMMARY) {
                    mergedFilters = { ...newFilters, objectId: undefined, subObjectId: undefined };

                    return mergedFilters;
                }

                mergedFilters = { ...oldFilters, ...newFilters };

                return mergedFilters;
            });

            handleSaveFilterState({
                key: String(constructionTypeId),
                newState: mergedFilters,
            });
        },
        [constructionTypeId, handleSaveFilterState],
    );

    const checkedWorkOrderNavs = useMemo(
        () => projectData?.contractNavigation?.data?.workOrderNavs?.find((workOrder) => workOrder.id === workOrderId),
        [projectData?.contractNavigation?.data?.workOrderNavs, workOrderId],
    );

    const titleWorkOrder = activeItemVersion
        ? activeItemVersion.labelWithoutChangeNumber.replace('НЗ', 'Наряд-Заказ')
        : getLabelWorkOrder(checkedWorkOrderNavs);

    const handleModalClose = useCallback(() => {
        const navState = { reload: Math.random() };

        setTimeout(() => navigate('./', { replace: true, state: navState }));
    }, [navigate]);

    const onActKCAddOrEdit = useCallback(() => {
        refreshProject();
        handleModalClose();
    }, [handleModalClose, refreshProject]);

    const statusWorkOrder = activeItemVersion
        ? (activeItemVersion?.item?.workOrderVersionItem as WorkOrderVersionItem)?.status
        : checkedWorkOrderNavs?.status;

    const handleBackVersion = useCallback(() => {
        // TODO: временно, до перехода на новое отображение
        if (pathname?.split('/')?.[6] === 'old') {
            navigate(`/projects/${methodology}/${projectId}/order/${orderId}/old`);
        } else {
            navigate(`/projects/${methodology}/${projectId}/order/${orderId}`);
        }
    }, [methodology, navigate, orderId, pathname, projectId]);

    const isAllAktKC2Accepted = useMemo(() => {
        const acceptedActs = checkedWorkOrderNavs?.actKC2Navs?.filter(
            ({ status }: ActKc2NavData) => status === ActKc2StatusEnum.Accepted,
        );

        return checkedWorkOrderNavs?.actKC2Navs?.length === acceptedActs?.length;
    }, [checkedWorkOrderNavs]);

    const tableRef = useRef<HTMLDivElement>(null);

    const isWorkOrderRequisitesDisabled = isSummaryChapterSelected || !isContractStatusWork || !orderRules.canEdit;

    const [seasons] = useWorkOrderVersionSeasons(workOrderVersionId);

    const handleNavigateStructure = useCallback(() => {
        navigate(`/projects/uer/${projectId}/order/${orderId}/version/${versionId}/structure`);
    }, [navigate, orderId, projectId, versionId]);

    return (
        <ProjectPageLayout
            title={titleWorkOrder}
            statusWorkOrder={statusWorkOrder}
            breadCrumbsItems={breadCrumbsItems}
            style={{ marginBottom: 0 }}
            isHeaderCollapsable
            layoutEntityKey={workOrderVersionId}
            headerBottom={
                <Toolbar flex>
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Вернуться к текущей версии"
                        view="secondary"
                        size="s"
                        onClick={handleBackVersion}
                        iconLeft={IconBackward}
                    />
                    <WorkOrderVersionRequisites
                        workOrderId={workOrderVersionId}
                        isDisabled={isWorkOrderRequisitesDisabled || isDisabledByProjectStatus}
                        refBtn={refBtn}
                        activeItemVersion={activeItemVersion}
                    />
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Просмотреть структуру"
                        view="secondary"
                        size="s"
                        onClick={handleNavigateStructure}
                    />
                    {!!workOrderVersionsData?.uerWorkOrderVersions?.length && (
                        <SelectVersionsWorkOrder
                            data={workOrderVersionsData?.uerWorkOrderVersions}
                            activeItemVersion={activeItemVersion}
                        />
                    )}
                </Toolbar>
            }
        >
            <div
                id="workOrder"
                style={
                    {
                        '--table-settings-height': `${TABLE_SETTINGS_HEIGHT}px`,
                        '--others-height':
                            isNumber(tableRef.current?.getBoundingClientRect?.()?.top) &&
                            `${
                                (tableRef.current?.getBoundingClientRect?.()?.top as number) +
                                TABLE_SETTINGS_HEIGHT +
                                PAGINATION_HEIGHT
                            }px`,
                    } as React.CSSProperties
                }
            >
                <WorkOrderConstructionTypesTabSelect
                    onChange={handleConstructionTypeChange}
                    constructionTypes={constructionTypes}
                    activeConstructionTypeId={constructionTypeId}
                />
                <Grid
                    cols={10}
                    gap="xl"
                    rowGap="xl"
                    colGap="xl"
                    className={cnMixSpace({ mT: 'xl', mB: 'xl' })}
                    style={{ overflow: 'auto' }}
                >
                    <GridItem col={6}>
                        <WorkOrderFilterForm
                            defaultValues={filters}
                            onChange={handleFilterFormChange}
                            constructionTypeId={constructionTypeId}
                            workOrderObjects={workOrderObjects}
                        />
                    </GridItem>
                </Grid>
                <div ref={tableRef}>
                    {isShowTablePossible ? (
                        <>
                            {isOneOfThePriceChaptersSelected && (
                                <WorkOrderVersionPrice
                                    workOrderId={workOrderVersionId}
                                    constructionTypeId={constructionTypeId}
                                    chapter={filters.chapter}
                                    workOrderObjectId={filters.objectId}
                                    workOrderSubObjectId={filters.subObjectId}
                                    reload={state?.reload}
                                    workOrderStatus={statusWorkOrder}
                                    contractStatus={getContractData?.status}
                                    isAllAktKC2Accepted={isAllAktKC2Accepted}
                                    onRefresh={onActKCAddOrEdit}
                                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                                    costRounding={
                                        projectData?.contractNavigation?.data?.contractNavData?.metadata
                                            ?.costRounding ?? 0
                                    }
                                />
                            )}
                            {isOthersChapterSelected && (
                                <WorkOrderVersionOtherTable
                                    section={filters.chapter}
                                    workOrderId={workOrderVersionId}
                                    workOrderObjectId={filters.objectId}
                                    workOrderStatus={statusWorkOrder}
                                    reload={state?.reload}
                                    contractStatus={getContractData?.status}
                                    isDisabledByProjectStatus={isDisabledByProjectStatus}
                                />
                            )}

                            {isSummaryChapterSelected && (
                                <WorkOrderVersionSummaryTable
                                    workOrderVersionId={workOrderVersionId}
                                    seasons={seasons}
                                    costRounding={
                                        projectData?.contractNavigation?.data?.contractNavData?.metadata
                                            ?.costRounding ?? 0
                                    }
                                />
                            )}
                        </>
                    ) : (
                        <ChooseSectionText text="Для отображения данных выберите раздел, объект и подобъект" />
                    )}
                </div>
            </div>
        </ProjectPageLayout>
    );
};

WorkOrderVersion.displayName = 'WorkOrderVersion';
